import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { ButtonModule } from '@fino-ui/button';
import { FinoIconModule } from '@fino-ui/icon';
import { ModalService } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize, Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ApproveModalComponent } from 'src/app/components/approve-modal/approve-modal.component';
import { AddUserComponent } from '../add-user/add-user.component';
import type { User } from './admin-users.model';
import { AdminUsersService } from './admin-users.service';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrl: './admin-users.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonModule,
    FinoIconModule,
    TranslationModule,
    NgxSkeletonLoaderModule,
  ],
})
export class AdminUsersComponent implements OnInit {
  constructor(
    private adminUsersService: AdminUsersService,
    private ngxModalService: ModalService,
    private appService: AppService,
  ) {}

  users: User[] = [];
  csvSubscription: Subscription = Subscription.EMPTY;
  createUserSubscription: Subscription = Subscription.EMPTY;
  loadUsersSubscription: Subscription = Subscription.EMPTY;

  ngOnInit(): void {
    this.fetchUsers();
  }

  get userLoading() {
    return !this.loadUsersSubscription.closed;
  }

  fetchUsers() {
    this.loadUsersSubscription = this.adminUsersService
      .fetchUsers()
      .subscribe((users: User[]) => {
        this.users = users;
      });
  }

  get csvDownloading() {
    return !this.csvSubscription?.closed;
  }

  get addUserLoading() {
    return !this.createUserSubscription?.closed;
  }

  downloadCSV() {
    this.csvSubscription = this.adminUsersService
      .downloadCSV()
      .subscribe(() => {});
  }

  deleteUser(user: User) {
    this.ngxModalService
      .addModal(ApproveModalComponent, {
        title: 'adminUsers.actions.deleteUser.title',
        description: 'adminUsers.actions.deleteUser.description',
      })
      .subscribe((approved) => {
        if (!approved) return;

        this.adminUsersService
          .deleteUser(user.id)
          .pipe(
            finalize(() => {
              this.fetchUsers();
            }),
          )
          .subscribe({
            next: () => {
              this.appService.success('adminUsers.actions.deleteUser.success');
            },
            error: () => {
              this.appService.error('adminUsers.actions.deleteUser.error');
            },
          });
      });
  }

  sendPasswordMail(user: User) {
    this.ngxModalService
      .addModal(ApproveModalComponent, {
        title: 'adminUsers.actions.sendPasswordMail.title',
        description: 'adminUsers.actions.sendPasswordMail.description',
      })
      .subscribe((approved) => {
        if (!approved) return;

        this.adminUsersService
          .sendPasswordMail(user.id)
          .pipe(
            finalize(() => {
              this.fetchUsers();
            }),
          )
          .subscribe({
            next: () => {
              this.appService.success(
                'adminUsers.actions.sendPasswordMail.success',
              );
            },
            error: () => {
              this.appService.error(
                'adminUsers.actions.sendPasswordMail.error',
              );
            },
          });
      });
  }

  addUser() {
    this.ngxModalService.addModal(AddUserComponent).subscribe((result) => {
      if (!result) return;
      this.createUserSubscription = this.adminUsersService
        .createUser(result)
        .pipe(
          finalize(() => {
            this.fetchUsers();
          }),
        )
        .subscribe({
          next: () => {
            this.appService.success('adminUsers.actions.addUser.success');
          },
          error: () => {
            this.appService.error('adminUsers.actions.addUser.error');
          },
        });
    });
  }
}
